<script module lang="ts">
  // Shared by every modal instance, including nested quick-create forms.
  const modalStack: symbol[] = [];
  let bodyOverflow = '';
</script>

<script lang="ts">
  import { X } from 'lucide-svelte';
  import { onMount } from 'svelte';
  import type { Snippet } from 'svelte';
  import { navigationGuards } from '$lib/state/navigationGuard';

  const {
    title,
    onClose,
    children,
    // Optional sticky action bar rendered *outside* the scrolling body, so
    // Save/Cancel stay reachable in a long form without scrolling to the
    // end of it. A submit button in here needs `form="<the form's id>"` —
    // it's a sibling of the <form>, not a descendant.
    footer,
    wide = false,
    // Overrides the wide/narrow tailwind max-width classes with an explicit
    // one (e.g. '900px') — only the email viewer needs more than `wide`'s
    // 720px so far.
    maxWidth
  }: {
    title: string;
    onClose: () => void;
    children: Snippet;
    footer?: Snippet;
    wide?: boolean;
    maxWidth?: string;
  } = $props();

  const id = Symbol('modal');
  const titleId = $props.id();
  let dialog: HTMLDivElement;
  let dirty = $state(false);
  let baseline = '';
  let confirming = $state(false);
  let discardPanel = $state<HTMLDivElement>();
  let resolveNavigation: ((accepted: boolean) => void) | undefined;
  const focusable = 'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]';
  const isTop = () => modalStack.at(-1) === id;
  function snapshot() {
    return JSON.stringify([...dialog.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('form input, form select, form textarea')]
      .filter(el => el.closest('[data-tym-modal]') === dialog && !(el instanceof HTMLInputElement && el.type === 'search'))
      .map(el => el instanceof HTMLInputElement && ['checkbox', 'radio'].includes(el.type) ? el.checked : el.value));
  }
  function isDirty() { return dirty && snapshot() !== baseline; }
  function onFormChange(e: Event) {
    const target = e.target as HTMLElement;
    if (target instanceof HTMLInputElement && target.type === 'search') return;
    if (target.closest('form') && target.closest('[data-tym-modal]') === dialog) dirty = true;
  }
  function cancelConfirmation() {
    const resolve = resolveNavigation;
    resolveNavigation = undefined;
    confirming = false;
    resolve?.(false);
  }
  function keepEditing() { cancelConfirmation(); dialog.focus(); }
  function discardAndClose() {
    dirty = false;
    baseline = snapshot();
    confirming = false;
    onClose();
  }
  function acceptDiscard() {
    if (!confirming) return;
    if (!resolveNavigation) { discardAndClose(); return; }
    const resolve = resolveNavigation;
    resolveNavigation = undefined;
    confirming = false;
    resolve(true);
  }
  function confirmNavigation(): Promise<boolean> {
    confirming = true;
    return new Promise(resolve => { resolveNavigation = resolve; });
  }

  function requestClose() {
    if (!isTop()) return;
    if (isDirty()) confirming = true;
    else onClose();
  }

  function captureClick(e: MouseEvent) {
    if (!isTop()) return;
    const target = (e.target as HTMLElement).closest('button');
    const form = target?.closest('form');
    const actionBar = target?.closest('.tym-modal-foot');
    // Existing form footers call their parent's close handler directly.
    // Route their Cancel affordance through the same dismissal guard.
    if (target?.closest('[data-tym-modal]') === dialog &&
      (form?.closest('[data-tym-modal]') === dialog || actionBar?.closest('[data-tym-modal]') === dialog) &&
      target.textContent?.trim() === 'Cancel' && !confirming) {
      e.preventDefault();
      e.stopPropagation();
      requestClose();
    }
  }

  function onKeydown(e: KeyboardEvent) {
    if (!isTop()) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopImmediatePropagation();
      if (confirming) keepEditing();
      else requestClose();
    }
    if (e.key === 'Tab') {
      const root = confirming && discardPanel ? discardPanel : dialog;
      const items = [...root.querySelectorAll<HTMLElement>(focusable)].filter(el => el.getClientRects().length > 0);
      const first = items[0];
      const last = items.at(-1);
      if (!first) { e.preventDefault(); dialog.focus(); }
      else if (e.shiftKey && (document.activeElement === first || !root.contains(document.activeElement))) {
        e.preventDefault(); last?.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !root.contains(document.activeElement))) {
        e.preventDefault(); first.focus();
      }
    }
  }

  $effect(() => {
    if (confirming && discardPanel) discardPanel.querySelector<HTMLElement>('button')?.focus();
  });

  onMount(() => {
    const previous = document.activeElement as HTMLElement | null;
    baseline = snapshot();
    if (!modalStack.length) bodyOverflow = document.body.style.overflow;
    modalStack.push(id);
    const unregisterGuard = navigationGuards.register({ isDirty, confirmDiscard: confirmNavigation, cancelConfirmation, discardAndClose });
    document.body.style.overflow = 'hidden';
    dialog.focus();
    // Capture prevents an underlying panel's Escape listener running first.
    window.addEventListener('keydown', onKeydown, true);
    const keepFocus = (e: FocusEvent) => {
      if (isTop() && !dialog.contains(e.target as Node)) dialog.focus();
    };
    window.addEventListener('focusin', keepFocus);
    return () => {
      unregisterGuard();
      modalStack.splice(modalStack.indexOf(id), 1);
      window.removeEventListener('keydown', onKeydown, true);
      window.removeEventListener('focusin', keepFocus);
      if (!modalStack.length) document.body.style.overflow = bodyOverflow;
      if (previous?.isConnected) previous.focus();
    };
  });
</script>

<!-- Below `sm` this is a bottom sheet (backdrop aligns the dialog to the
     bottom edge, full-bleed, rounded top corners, slide-up); from `sm` up
     it stays the centered dialog it has always been. -->
<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  class="fixed inset-0 z-40 flex items-end justify-center overflow-y-auto bg-[#0A1626]/50 backdrop-blur-[2px] sm:items-center sm:px-4 sm:py-10"
  role="presentation"
  onclick={requestClose}
>
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <div
    bind:this={dialog}
    data-tym-modal=""
    class="tym-modal-dialog w-full {wide
      ? 'max-w-[720px]'
      : 'max-w-[460px]'} rounded-t-[20px] bg-white shadow-[0_24px_64px_rgba(16,34,58,.28)] sm:rounded-[18px]"
    style={maxWidth ? `max-width: ${maxWidth}` : undefined}
    role="dialog"
    aria-modal="true"
    aria-labelledby={titleId}
    tabindex="-1"
    onclick={(e) => e.stopPropagation()}
    onclickcapture={captureClick}
    oninput={onFormChange}
    onchange={onFormChange}
  >
    <div class="tym-sheet-grabber sm:hidden"></div>
    <div class="flex shrink-0 items-center justify-between p-5 pb-4 sm:p-[26px] sm:pb-4">
      <h2 id={titleId} class="font-heading text-lg font-bold text-navy">{title}</h2>
      <button
        type="button"
        onclick={requestClose}
        class="-mr-2 flex h-11 w-11 shrink-0 items-center justify-center text-dark-gray hover:text-navy sm:mr-0 sm:h-auto sm:w-auto"
        aria-label="Close"
      >
        <X class="h-5 w-5" />
      </button>
    </div>
    <div
      class="tym-modal-body px-5 pb-5 sm:px-[26px] sm:pb-[26px] {footer ? '' : 'tym-modal-body-safe'}"
    >
      {@render children()}
    </div>
    {#if footer}
      <div class="tym-modal-foot">
        {@render footer()}
      </div>
    {/if}
    {#if confirming}
      <div bind:this={discardPanel} role="alertdialog" aria-label="Discard unsaved changes?" class="border-t border-amber-200 bg-amber-50 p-5">
        <p class="mb-3 text-sm font-semibold text-navy">Discard unsaved changes?</p>
        <div class="flex gap-2">
          <button type="button" class="btn-secondary" onclick={keepEditing}>Keep editing</button>
          <button type="button" class="btn-danger" onclick={acceptDiscard}>Discard changes</button>
        </div>
      </div>
    {/if}
  </div>
</div>
